import { useCallback, useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { Page } from '@src/components';
import {
  adjustDesignerBank,
  createDesignerBank,
  depositDesignerBank,
  fetchDesignerBankLedger,
  listDesignerBanks,
  listDesignerGroups,
  updateDesignerBank,
} from '@src/api/creativeStudio';
import { useMessage } from '@src/context/common';

const getResults = (payload) => {
  if (Array.isArray(payload)) return payload;
  return payload?.results || [];
};

const money = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const KIND_CHIP = {
  deposit: { label: 'Deposit', color: 'success' },
  debit_ad_approved: { label: 'Ad approved', color: 'default' },
  adjustment: { label: 'Adjustment', color: 'warning' },
};

const emptyBankForm = { name: 'Designer Bank', designer_group: '', cost_per_ad: '', low_balance_ads_threshold: '10' };
const emptyDepositForm = { amount: '', paid_on: new Date().toISOString().slice(0, 10), note: '' };
const emptyAdjustForm = { amount: '', note: '' };

function DesignerBankPage() {
  const message = useMessage();
  const [loading, setLoading] = useState(true);
  const [banks, setBanks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // dialog = { type: 'create'|'edit'|'deposit'|'adjust', bank }
  const [dialog, setDialog] = useState(null);
  const [bankForm, setBankForm] = useState(emptyBankForm);
  const [depositForm, setDepositForm] = useState(emptyDepositForm);
  const [adjustForm, setAdjustForm] = useState(emptyAdjustForm);

  const loadBanks = useCallback(async () => {
    setLoading(true);
    try {
      const [banksResp, groupsResp] = await Promise.all([listDesignerBanks(), listDesignerGroups()]);
      const rows = getResults(banksResp?.data);
      setBanks(rows);
      setGroups(getResults(groupsResp?.data));
      setSelectedBankId((prev) => prev || rows[0]?.id || null);
    } catch (error) {
      console.error('Failed to load designer banks', error);
      message.error('Unable to load designer banks.');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    loadBanks();
  }, [loadBanks]);

  useEffect(() => {
    if (!selectedBankId) {
      setLedger([]);
      return;
    }
    let active = true;
    setLedgerLoading(true);
    fetchDesignerBankLedger(selectedBankId)
      .then((resp) => {
        if (active) setLedger(getResults(resp?.data));
      })
      .catch((error) => {
        console.error('Failed to load ledger', error);
        if (active) message.error('Unable to load bank history.');
      })
      .finally(() => {
        if (active) setLedgerLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selectedBankId, message]);

  const refreshAll = async () => {
    await loadBanks();
    if (selectedBankId) {
      const resp = await fetchDesignerBankLedger(selectedBankId).catch(() => null);
      if (resp) setLedger(getResults(resp?.data));
    }
  };

  const openCreate = () => {
    setBankForm(emptyBankForm);
    setDialog({ type: 'create' });
  };

  const openEdit = (bank) => {
    setBankForm({
      name: bank.name || '',
      designer_group: bank.designer_group || '',
      cost_per_ad: String(bank.cost_per_ad ?? ''),
      low_balance_ads_threshold: String(bank.low_balance_ads_threshold ?? '10'),
    });
    setDialog({ type: 'edit', bank });
  };

  const openDeposit = (bank) => {
    setDepositForm(emptyDepositForm);
    setDialog({ type: 'deposit', bank });
  };

  const openAdjust = (bank) => {
    setAdjustForm(emptyAdjustForm);
    setDialog({ type: 'adjust', bank });
  };

  const closeDialog = () => setDialog(null);

  const errorDetail = (error, fallback) =>
    error?.response?.data?.detail ||
    (typeof error?.response?.data === 'object' ? Object.values(error.response.data).flat().join(' ') : '') ||
    fallback;

  const submitBankForm = async () => {
    setSubmitting(true);
    const payload = {
      name: bankForm.name.trim() || 'Designer Bank',
      designer_group: bankForm.designer_group || null,
      cost_per_ad: bankForm.cost_per_ad || '0',
      low_balance_ads_threshold: Number(bankForm.low_balance_ads_threshold) || 0,
    };
    try {
      if (dialog.type === 'create') {
        const resp = await createDesignerBank(payload);
        setSelectedBankId(resp?.data?.id || null);
        message.success('Bank created.');
      } else {
        await updateDesignerBank(dialog.bank.id, payload);
        message.success('Bank updated.');
      }
      closeDialog();
      await refreshAll();
    } catch (error) {
      console.error('Failed to save bank', error);
      message.error(errorDetail(error, 'Unable to save bank.'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitDeposit = async () => {
    setSubmitting(true);
    try {
      await depositDesignerBank(dialog.bank.id, {
        amount: depositForm.amount,
        paid_on: depositForm.paid_on || undefined,
        note: depositForm.note.trim(),
      });
      message.success('Deposit recorded.');
      closeDialog();
      await refreshAll();
    } catch (error) {
      console.error('Failed to record deposit', error);
      message.error(errorDetail(error, 'Unable to record deposit.'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitAdjust = async () => {
    setSubmitting(true);
    try {
      await adjustDesignerBank(dialog.bank.id, {
        amount: adjustForm.amount,
        note: adjustForm.note.trim(),
      });
      message.success('Adjustment recorded.');
      closeDialog();
      await refreshAll();
    } catch (error) {
      console.error('Failed to record adjustment', error);
      message.error(errorDetail(error, 'Unable to record adjustment.'));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedBank = banks.find((b) => b.id === selectedBankId) || null;

  return (
    <Page title="Designer Bank">
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="body2" color="text.secondary">
            Prepaid designer balance — each approved ad deducts the cost per ad automatically.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} onClick={refreshAll}>Refresh</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New bank</Button>
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : banks.length === 0 ? (
          <Alert severity="info" icon={<SavingsOutlinedIcon />}>
            No bank yet. Create one, set the cost per ad, and record your first deposit — approvals
            will start deducting automatically.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {banks.map((bank) => (
              <Grid item xs={12} md={6} lg={4} key={bank.id}>
                <Card
                  variant="outlined"
                  onClick={() => setSelectedBankId(bank.id)}
                  sx={{
                    cursor: 'pointer',
                    borderColor: bank.id === selectedBankId ? 'primary.main' : undefined,
                    borderWidth: bank.id === selectedBankId ? 2 : 1,
                  }}
                >
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>{bank.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {bank.designer_group_name || 'Default (all designers)'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        {!bank.active && <Chip size="small" label="Inactive" />}
                        {bank.is_low && <Chip size="small" color="error" label="Low balance" />}
                      </Stack>
                    </Stack>
                    <Typography variant="h4" sx={{ mt: 1.5 }} color={Number(bank.balance) < 0 ? 'error.main' : 'text.primary'}>
                      {money(bank.balance)}
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
                      <Typography variant="body2" color="text.secondary">
                        {money(bank.cost_per_ad)} / ad
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {bank.ads_remaining === null || bank.ads_remaining === undefined
                          ? 'Set cost per ad'
                          : `${bank.ads_remaining} ads left`}
                      </Typography>
                    </Stack>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Typography variant="caption" color="text.secondary">
                        Paid in: {money(bank.total_deposited)}
                        {bank.last_deposit_at ? ` (last ${new Date(bank.last_deposit_at).toLocaleDateString()})` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {bank.ads_charged} ads charged ({money(bank.total_charged)})
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                      <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); openDeposit(bank); }}>
                        Record payment
                      </Button>
                      <Button size="small" onClick={(e) => { e.stopPropagation(); openAdjust(bank); }}>
                        Adjust
                      </Button>
                      <Tooltip title="Edit name, group, cost per ad">
                        <Button size="small" startIcon={<EditOutlinedIcon />} onClick={(e) => { e.stopPropagation(); openEdit(bank); }}>
                          Edit
                        </Button>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {selectedBank && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                History — {selectedBank.name}
              </Typography>
              {ledgerLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : ledger.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No activity yet.</Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 480 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="right">Balance after</TableCell>
                        <TableCell>Detail</TableCell>
                        <TableCell>By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ledger.map((entry) => {
                        const chip = KIND_CHIP[entry.kind] || { label: entry.kind_display, color: 'default' };
                        const detailParts = [];
                        if (entry.task_code) detailParts.push(entry.task_code);
                        if (entry.designer_name) detailParts.push(entry.designer_name);
                        if (entry.paid_on) detailParts.push(`paid ${entry.paid_on}`);
                        if (entry.note) detailParts.push(entry.note);
                        return (
                          <TableRow key={entry.id} hover>
                            <TableCell>{new Date(entry.created_at).toLocaleString()}</TableCell>
                            <TableCell>
                              <Chip size="small" label={chip.label} color={chip.color} />
                            </TableCell>
                            <TableCell align="right" sx={{ color: Number(entry.amount) < 0 ? 'error.main' : 'success.main', fontWeight: 600 }}>
                              {money(entry.amount)}
                            </TableCell>
                            <TableCell align="right">{money(entry.balance_after)}</TableCell>
                            <TableCell sx={{ maxWidth: 360 }}>{detailParts.join(' · ') || '—'}</TableCell>
                            <TableCell>{entry.created_by_name || '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}
      </Stack>

      {/* Create / edit bank */}
      <Dialog open={dialog?.type === 'create' || dialog?.type === 'edit'} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{dialog?.type === 'create' ? 'New designer bank' : 'Edit bank'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={bankForm.name}
              onChange={(e) => setBankForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
            />
            <TextField
              select
              label="Designer group"
              value={bankForm.designer_group}
              onChange={(e) => setBankForm((f) => ({ ...f, designer_group: e.target.value }))}
              helperText="Leave as Default to cover every approval without a group-specific bank."
              fullWidth
            >
              <MenuItem value="">Default (all designers)</MenuItem>
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Cost per ad (USD)"
              type="number"
              value={bankForm.cost_per_ad}
              onChange={(e) => setBankForm((f) => ({ ...f, cost_per_ad: e.target.value }))}
              helperText="Deducted every time an ad is approved. Change it any time — past charges keep their rate."
              fullWidth
            />
            <TextField
              label="Low-balance warning (ads remaining)"
              type="number"
              value={bankForm.low_balance_ads_threshold}
              onChange={(e) => setBankForm((f) => ({ ...f, low_balance_ads_threshold: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={submitBankForm} disabled={submitting}>
            {dialog?.type === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deposit */}
      <Dialog open={dialog?.type === 'deposit'} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Record designer payment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Amount paid (USD)"
              type="number"
              autoFocus
              value={depositForm.amount}
              onChange={(e) => setDepositForm((f) => ({ ...f, amount: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Date paid"
              type="date"
              value={depositForm.paid_on}
              onChange={(e) => setDepositForm((f) => ({ ...f, paid_on: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Note (optional)"
              value={depositForm.note}
              onChange={(e) => setDepositForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="e.g. Wire #1042 to studio"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={submitDeposit} disabled={submitting || !depositForm.amount}>
            Record payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adjust */}
      <Dialog open={dialog?.type === 'adjust'} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Manual adjustment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Amount (USD, negative to deduct)"
              type="number"
              autoFocus
              value={adjustForm.amount}
              onChange={(e) => setAdjustForm((f) => ({ ...f, amount: e.target.value }))}
              helperText="Use for corrections, e.g. refunding a charge or fixing a typo'd deposit."
              fullWidth
            />
            <TextField
              label="Reason (required)"
              value={adjustForm.note}
              onChange={(e) => setAdjustForm((f) => ({ ...f, note: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitAdjust}
            disabled={submitting || !adjustForm.amount || !adjustForm.note.trim()}
          >
            Apply adjustment
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

export default DesignerBankPage;
